import environment from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as qs from 'qs';
import { ArticleType } from '../interfaces/strapi';

@Injectable({
  providedIn: 'root',
})
export default class StrapiService {
  private strapiUrl: string;

  constructor(private http: HttpClient) {
    this.strapiUrl = environment.strapi.url;
  }

  public getDocumentByUID(
    type: string,
    uid: string,
    params: Record<string, unknown> = {}
  ): Observable<any> {
    const paramsWithFilters = {
      ...params,
      filters: {
        ...(params as { filters?: Record<string, unknown> }).filters,
        uid: { $eq: uid },
      },
    };

    const queryParams = this.createQueryParams(paramsWithFilters);

    const url = queryParams
      ? `${this.strapiUrl}/${type}?${queryParams}`
      : `${this.strapiUrl}/${type}`;

    return this.http.get(url);
  }

  public getDocumentsByType(
    type: string,
    params: Record<string, unknown> = {}
  ): Observable<any> {
    const queryString = this.createQueryParams(params)
    const url = queryString
      ? `${this.strapiUrl}/${type}?${queryString}`
      : `${this.strapiUrl}/${type}`;

    return this.http.get(url);
  }

  public getProtectAndSave() {
    const params = {
      populate: {
        tab: {
          populate: {
            product: {
              populate: {
                image: true,
                more_info: {
                  populate: {
                    image: true,
                    tabs: true,
                    dashboard_title_logo: true,
                    dashboard_card_logo: true,
                    dashboard_card_buddy: true
                  }
                }
              }
            }
          }
        },
        banner: {
          populate: {
            slides: { populate: '*' },
            background_image_web: true
          }
        },
        background_image_web: true
      }
    };
    return this.getDocumentsByType('protect-and-save', params);
  }

  public getProtectAndSaveProducts() {
    const params = {
      populate: {
        tabs: true,
        image: true,
        dashboard_title_logo: true,
        dashboard_card_logo: true,
        dashboard_card_buddy: true
      }
    };
    return this.getDocumentsByType('protect-and-save-products', params);
  }

  public getArticle(uid: string, articleType: ArticleType): Observable<any> {
    const articleTypeId =
      articleType === 'STUDENTLIFE'
        ? 'student-life-articles'
        : 'wellbeing-articles';

    return this.getDocumentByUID(articleTypeId, uid, { populate: '*' });
  }

  public getDashboard(): Observable<any> {
    const dashboardParams = {
      populate: {
        banner: {
          populate: {
            slides: { populate: ['image'] },
            background_image_web: true
          }
        },
        banner_alt: {
          populate: {
            slides: { populate: ['image'] },
            background_image_web: true
          }
        },
        cyc_banner: { populate: '*' },
        IS_carousel: {
          populate: {
            slides: { populate: ['image'] },
            background_image_web: true
          }
        },
        IS_carousel_alt: {
          populate: {
            slides: { populate: ['image'] },
            background_image_web: true
          }
        },
        student_guard_banner: {
          populate: ['image']
        }
      }
    };

    return this.getDocumentsByType('dashboard', dashboardParams);
  }

  public getInternationalStudentPage(): Observable<any> {
    const params = {
      populate: {
        make_a_claim_cards: { populate: '*' },
        header_image_web: { populate: '*' },
        help_image_web: { populate: '*' },
        student_life_banner: { populate: '*' },
      }
    };
    return this.getDocumentsByType('international-students-page', params);
  }

  public getOffers(): Observable<any> {
    return this.getDocumentsByType('offer', {
      populate: {
        cards: { populate: '*' }
      }
    });
  }

  public getFooter(): Observable<any> {
    const params = {
      populate: {
        apple_store_image: { populate: '*' },
        google_store_image: { populate: '*' },
        social_links: { populate: ['image'] },
        helpful_links: { populate: '*' },
        legal_links: { populate: '*' }
      }
    };
    return this.getDocumentByUID('footer', 'footer', params);
  }

  public getHeaders(): Observable<any> {
    return this.getDocumentByUID('headers', 'header', {
      populate: {
        logo: { populate: '*' }
      }
    });
  }

  public getMainMobileMenu(): Observable<any> {
    return this.getDocumentByUID('menus', 'main-mobile', { populate: '*' });
  }

  public getNavigationMenubar(): Observable<any> {
    return this.getDocumentByUID('menus', 'navigation-bar', { populate: '*' });
  }

  public getStudentLifePage(): Observable<any> {
    return this.getDocumentsByType('student-life', { populate: '*' });
  }

  public getInternationalStudentContactForm(): Observable<any> {
    return this.getDocumentsByType('international-students-claim-form', {
      populate: '*',
    });
  }

  public getInternationalStudentContactUs(): Observable<any> {
    return this.getDocumentsByType('international-student-contact-us', {
      populate: '*',
    });
  }

  public getPolicySummary(): Observable<any> {
    return this.getDocumentsByType('policy-summary', {
      populate: {
        languages: {
          populate: {
            policy_document: {
              populate: ['file']
            }
          }
        }
      }
    });
  }

  public getMyInsurance(): Observable<any> {
    return this.getDocumentByUID('your-insurances', 'your-insurance', {
      populate: '*',
    });
  }

  public getStudentLifeSustainabilityBanner(): Observable<any> {
    return this.getDocumentsByType('sustainability', {
      populate: {
        student_life_banner: {
          populate: ['image']
        }
      }
    });
  }

  public getStudentLifeJACBanner(): Observable<any> {
    return this.getDocumentsByType('jobs-and-careers', {
      populate: {
        student_life_banner: {
          populate: ['image']
        }
      }
    });
  }

  public getJobsAndCareersCarousel(): Observable<any> {
    return this.getDocumentsByType('jobs-and-careers-page', {
      populate: {
        dashboard_cards: { populate: '*' }
      }
    });
  }

  public getJobsAndCareers(): Observable<any> {
    const params = {
      populate: {
        tabs: {
          populate: {
            si_banner: { populate: '*' },
            header_image_web: true,
            cards: true,
            header_image_app: true
          }
        }
      }
    };
    return this.getDocumentsByType('jobs-and-careers-page', params);
  }

  public getStudentLifeFilter(): Observable<any> {
    return this.getDocumentByUID(
      'student-life-filters',
      'student_life_filters',
      {
        populate: '*',
      }
    );
  }

  public getStudentLifeArticles(): Observable<any> {
    return this.getDocumentsByType('student-life-articles', {
      populate: {
        header_image: true,
        card_image: true,
      }
    });
  }

  public getRegisterPage(): Observable<any> {
    return this.getDocumentByUID('register', 'register');
  }

  public getRegistrationEmailPage(): Observable<any> {
    return this.getDocumentsByType('registration-email', { populate: '*' });
  }

  public getUpdateDetails(): Observable<any> {
    return this.getDocumentsByType('update-detail', { populate: '*' });
  }

  public getUserUpdateChoice(uid: string): Observable<any> {
    return this.getDocumentByUID('user-update-choices', uid);
  }

  public getLoginPage(): Observable<any> {
    return this.getDocumentByUID('login', 'login');
  }

  public getResetPassword(): Observable<any> {
    return this.getDocumentsByType('reset-password', { populate: '*' });
  }

  public getCarousel(uid: string): Observable<any> {
    const params = {
      populate: {
        slides: { populate: ['image'] },
        background_image_app: true
      }
    };
    return this.getDocumentByUID('carousels', uid, params);
  }

  public getProfile(): Observable<any> {
    return this.getDocumentsByType('profile', { populate: 'header_image' });
  }

  public getPolicyDocumentYearChange(): Observable<any> {
    return this.getDocumentsByType('policy-document-year-change');
  }

  private createQueryParams(params: Record<string, unknown>): string {
    if (Object.keys(params).length === 0) {
      return '';
    }

    return qs.stringify(params, { encodeValuesOnly: true });
  }
}
